import { logError } from "@lib/logger";
import { sharedStyles } from "@lib/sharedStyles";
import {
	CategoryScale,
	Chart,
	Legend,
	LineController,
	LineElement,
	LinearScale,
	PointElement,
	Title,
	Tooltip,
} from "chart.js";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

Chart.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	LineController
);

@customElement("line-chart")
export class LineChart extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`
      .chart-container {
        width: 100%;
        height: 240px;
        position: relative;
      }
      #myChart {
        width: 100%;
        height: 100%;
        margin: 8px;
      }
    `,
	];

	@property({ type: Object }) lineChartProps: any = { title: "", labels: [], datasets: [], options: {} };
	private chart: Chart | null = null;

	constructor(lineChartProps?: any) {
		super();
		if (lineChartProps) this.lineChartProps = lineChartProps;
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this._resetChart();
	}

	firstUpdated() {
		this._renderChart(this.lineChartProps);
	}

	updated(changed: Map<string, unknown>) {
		if (changed.has("lineChartProps")) {
			this._renderChart(this.lineChartProps);
		}
	}

	private _renderChart(props: any) {
		try {
			const canvas = this._getCanvas(this.shadowRoot!);
			this._resetChart();
			const ctx = this._getCtx(canvas);
			this.chart = new Chart(ctx, {
				type: "line",
				data: {
					labels: props.labels,
					datasets: props.datasets,
				},
				options: props.options,
			});
		} catch (e) {
			logError("Failed to create line chart:", e);
			alert("Failed to create chart");
		}
	}

	private _getCanvas(root: ShadowRoot) {
		const el = root.querySelector("#myChart") as HTMLCanvasElement | null;
		if (!el) throw new Error("Canvas element not found");
		return el;
	}

	private _getCtx(canvas: HTMLCanvasElement) {
		const ctx = canvas.getContext("2d");
		if (!ctx) throw new Error("Could not get 2D context");
		return ctx;
	}

	private _resetChart() {
		if (this.chart) {
			this.chart.destroy();
			this.chart = null;
		}
	}

	render() {
		return html`
      <div style="display:flex;flex-direction:column;gap:16px;align-items:center;">
        ${this.lineChartProps.title ? html`<h1>${this.lineChartProps.title}</h1>` : null}
        <div class="chart-container">
          <canvas id="myChart"></canvas>
        </div>
      </div>
    `;
	}
}

export default LineChart;
